/**
 * StoriesPage - dedicated Stories hub: create + browse network stories
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Camera } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { StoryViewer, StoryCreator } from '../components/Stories';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StoriesPage = () => {
  const navigate = useNavigate();
  const { token, user, isAuthenticated } = useAuth();
  const [myStories, setMyStories] = useState([]);
  const [storyUsers, setStoryUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showViewer, setShowViewer] = useState(null);
  const [showCreator, setShowCreator] = useState(false);

  const fetchStories = async () => {
    if (!token) { setLoading(false); return; }
    try {
      const [feedRes, myRes] = await Promise.all([
        fetch(`${API_URL}/api/radio/stories/feed`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/radio/stories/my`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const feedData = await feedRes.json();
      const myData = await myRes.json();
      setStoryUsers(feedData.users || []);
      setMyStories(myData.stories || []);
    } catch (err) {
      console.error('Fetch stories error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStories(); /* eslint-disable-next-line */ }, [token]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-6 text-center pb-24">
        <Camera className="w-14 h-14 text-[#52525B] mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Sign in to view Stories</h2>
        <button onClick={() => navigate('/auth')} className="mt-2 px-6 py-3 rounded-xl bg-[#FF007F] text-white font-medium">
          Sign In
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-24" data-testid="stories-page">
      <div className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5 px-4 py-4">
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Stories</h1>
      </div>

      <div className="px-4 py-6 space-y-6">
        {/* Create CTA */}
        <button
          onClick={() => setShowCreator(true)}
          className="w-full flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-[#FF007F]/20 to-[#8B5CF6]/15 border border-white/10 hover:border-white/20 transition-colors"
          data-testid="stories-page-create-btn"
        >
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF007F] to-[#8B5CF6] flex items-center justify-center flex-shrink-0">
            <Plus className="w-6 h-6 text-white" strokeWidth={3} />
          </div>
          <div className="text-left">
            <p className="text-white font-semibold">Create a Story</p>
            <p className="text-[#71717A] text-sm">Photo or video, up to 30 seconds</p>
          </div>
        </button>

        {/* Your stories */}
        {myStories.length > 0 && (
          <div>
            <h2 className="text-sm font-medium text-[#A1A1AA] mb-3">Your Story</h2>
            <button
              onClick={() => setShowViewer({
                user_id: user?.id,
                user_name: user?.name || user?.stageName || 'You',
                user_photo: user?.profilePhoto,
                is_artist: true,
                artist_name: user?.stageName,
                stories: myStories
              })}
              className="flex items-center gap-3"
              data-testid="my-story-item"
            >
              <div className="p-0.5 rounded-full" style={{ background: 'linear-gradient(135deg,#8B5CF6,#EC4899)' }}>
                <div className="w-14 h-14 rounded-full overflow-hidden bg-[#12101A] border-2 border-[#050505]">
                  {user?.profilePhoto
                    ? <img src={user.profilePhoto} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-white font-bold">{(user?.name || 'Y')[0]}</div>}
                </div>
              </div>
              <span className="text-white text-sm">{myStories.length} active</span>
            </button>
          </div>
        )}

        {/* Network stories */}
        <div>
          <h2 className="text-sm font-medium text-[#A1A1AA] mb-3">From your network</h2>
          {loading ? (
            <div className="flex gap-3">
              {[...Array(4)].map((_, i) => <div key={i} className="w-16 h-16 rounded-full bg-white/5 animate-pulse" />)}
            </div>
          ) : storyUsers.length > 0 ? (
            <div className="grid grid-cols-4 gap-3" data-testid="stories-grid">
              {storyUsers.map((s) => (
                <button key={s.user_id} onClick={() => setShowViewer(s)} className="flex flex-col items-center gap-1">
                  <div className={`p-0.5 rounded-full ${s.has_unviewed ? 'animate-pulse' : ''}`}
                    style={{ background: s.has_unviewed ? `linear-gradient(135deg, ${s.glow_color}, ${s.glow_color}88)` : 'rgba(255,255,255,0.1)' }}>
                    <div className="w-14 h-14 rounded-full overflow-hidden bg-[#12101A] border-2 border-[#050505]">
                      {s.user_photo
                        ? <img src={s.user_photo} alt="" className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-[#A1A1AA] font-bold">{(s.user_name || '?')[0]}</div>}
                    </div>
                  </div>
                  <span className="text-[#A1A1AA] text-[10px] truncate w-16 text-center">{s.artist_name || s.user_name}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-[#71717A] text-sm">No stories from people you follow yet.</p>
          )}
        </div>
      </div>

      {showViewer && (
        <StoryViewer storyUser={showViewer} onClose={() => setShowViewer(null)} onStoryDeleted={fetchStories} />
      )}
      {showCreator && (
        <StoryCreator onClose={() => setShowCreator(false)} onSuccess={() => { setShowCreator(false); fetchStories(); }} />
      )}
    </div>
  );
};

export default StoriesPage;
